import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Form, Alert, Spinner, Row, Col } from 'react-bootstrap';
import UpdateLayout from '../../layouts/UpdateLayout';
import AppScriptAPI from '../../services/appscriptAPI';
import { useUpdatePermissions } from '../../hooks/useUpdatePermissions';
import { useConsentModal } from '../../hooks/useConsentModal';
import AppToast from '../../components/AppToast';
import SelectWithOther from '../../components/SelectWithOther';

function UpdateVocational() {
  const { recordId } = useParams();
  const [searchParams] = useSearchParams();
  const sheetId = searchParams.get('sheetId');
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: '', type: '' });
  const { canEdit } = useUpdatePermissions();
  const { confirmWithConsent, ConsentModalJSX } = useConsentModal();

  const [recordData, setRecordData] = useState(null);
  const [originalData, setOriginalData] = useState(null);
  const [legendData, setLegendData] = useState(null);

  const [formData, setFormData] = useState({
    uniqueId: '',
    name: '',
    vocationalStatus: '',
    trainingKind: '',
    organizationName: '',
    govOrganizations: [],
    otherGovOrganization: '',
    dossInstitutions: [],
    ministryInstitutions: [],
    completedDossCourses: [],
    completedMinistryCourses: '',
    relatedOccupation: '',
    natureOfProfession: '',
    unrelatedReason: '',
    discontinuedReason: '',
    otherDiscontinuedReason: '',
    notFollowingReason: '',
    otherNotFollowingReason: '',
    hasDifficulty: '',
    difficultyReasons: [],
    otherDifficultyReason: '',
    needsCareerGuidance: '',
  });

  useEffect(() => {
    if (!recordId || !sheetId) {
      setAlert({
        show: true,
        message: 'Missing required parameters.',
        type: 'danger',
      });
      setLoading(false);
      return;
    }
    loadRecordData();
  }, [recordId, sheetId]);

  const loadRecordData = async () => {
    try {
      const response = await AppScriptAPI.getVocationalTrainingDetails(
        recordId,
        sheetId
      );

      if (!response.success) {
        setAlert({
          show: true,
          message:
            response.message || 'Failed to load vocational training details.',
          type: 'danger',
        });
        setLoading(false);
        return;
      }

      setRecordData(response.record);
      setLegendData(response.legend);

      const parseArray = (str) =>
        str ? str.split(',').map((s) => s.trim()).filter((s) => s) : [];

      const parseArrayWithOther = (str) => {
        if (!str) return { array: [], otherValue: '' };
        const items = str.split(',').map((s) => s.trim()).filter((s) => s);
        const otherItem = items.find((item) => item.startsWith('Other:'));
        if (otherItem) {
          return {
            array: items.filter((item) => !item.startsWith('Other:')).concat(['Other']),
            otherValue: otherItem.replace('Other:', '').trim(),
          };
        }
        return { array: items, otherValue: '' };
      };

      const govOrgsResult = parseArrayWithOther(response.record.govOrganizations);

      const data = {
        uniqueId: response.record.uniqueId || '',
        name: response.record.name || '',
        vocationalStatus: response.record.vocationalStatus || '',
        trainingKind: response.record.trainingKind || '',
        organizationName: response.record.organizationName || '',
        govOrganizations: govOrgsResult.array,
        otherGovOrganization: govOrgsResult.otherValue,
        dossInstitutions: parseArray(response.record.dossInstitutions),
        ministryInstitutions: parseArray(response.record.ministryInstitutions),
        completedDossCourses: parseArray(response.record.completedDossCourses),
        completedMinistryCourses: response.record.completedMinistryCourses || '',
        relatedOccupation: response.record.relatedOccupation || '',
        natureOfProfession: response.record.natureOfProfession || '',
        unrelatedReason: response.record.unrelatedReason || '',
        discontinuedReason: (() => {
          const val = response.record.discontinuedReason || '';
          const opts = response.legend?.discontinuedReason || [];
          return val && !opts.includes(val) ? 'Other' : val;
        })(),
        otherDiscontinuedReason: (() => {
          const val = response.record.discontinuedReason || '';
          const opts = response.legend?.discontinuedReason || [];
          return val && !opts.includes(val) ? val : '';
        })(),
        notFollowingReason: (() => {
          const val = response.record.notFollowingReason || '';
          const opts = response.legend?.notFollowingReason || [];
          return val && !opts.includes(val) ? 'Other' : val;
        })(),
        otherNotFollowingReason: (() => {
          const val = response.record.notFollowingReason || '';
          const opts = response.legend?.notFollowingReason || [];
          return val && !opts.includes(val) ? val : '';
        })(),
        hasDifficulty: response.record.hasDifficulty || '',
        difficultyReasons: parseArrayWithOther(response.record.difficultyReasons).array,
        otherDifficultyReason: parseArrayWithOther(response.record.difficultyReasons).otherValue,
        needsCareerGuidance: response.record.needsCareerGuidance || '',
      };

      setFormData(data);
      setOriginalData(JSON.parse(JSON.stringify(data)));
      setLoading(false);
    } catch (error) {
      setAlert({
        show: true,
        message: 'An error occurred: ' + error.message,
        type: 'danger',
      });
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    let updates = { [name]: value };

    // Handle vocational status change
    if (name === 'vocationalStatus') {
      // Clear conditional fields based on status
      if (!['Completed', 'Currently doing', 'Discontinued'].includes(value)) {
        updates.trainingKind = '';
        updates.organizationName = '';
        updates.govOrganizations = [];
        updates.otherGovOrganization = '';
        updates.dossInstitutions = [];
        updates.ministryInstitutions = [];
        updates.completedDossCourses = [];
        updates.completedMinistryCourses = '';
      }
      if (value !== 'Completed') {
        updates.relatedOccupation = '';
        updates.natureOfProfession = '';
        updates.unrelatedReason = '';
      }
      if (value !== 'Discontinued') {
        updates.discontinuedReason = '';
        updates.otherDiscontinuedReason = '';
      }
      if (value !== 'Not joined') {
        updates.notFollowingReason = '';
        updates.otherNotFollowingReason = '';
        updates.hasDifficulty = '';
        updates.difficultyReasons = [];
        updates.otherDifficultyReason = '';
      }
    }

    // Handle training kind change
    if (name === 'trainingKind') {
      if (value !== 'NGO' && value !== 'Private') {
        updates.organizationName = '';
      }
      if (value !== 'Government') {
        updates.govOrganizations = [];
        updates.otherGovOrganization = '';
        updates.dossInstitutions = [];
        updates.ministryInstitutions = [];
        updates.completedDossCourses = [];
        updates.completedMinistryCourses = '';
      }
    }

    // Handle related occupation change
    if (name === 'relatedOccupation') {
      if (value !== 'Yes') {
        updates.natureOfProfession = '';
      }
      if (value !== 'Engages in unrelated occupation') {
        updates.unrelatedReason = '';
      }
    }

    // Handle difficulty change
    if (name === 'hasDifficulty' && value !== 'Yes') {
      updates.difficultyReasons = [];
      updates.otherDifficultyReason = '';
    }

    if (name === 'discontinuedReason' && value !== 'Other') {
      updates.otherDiscontinuedReason = '';
    }

    if (name === 'notFollowingReason' && value !== 'Other') {
      updates.otherNotFollowingReason = '';
    }

    setFormData({
      ...formData,
      ...updates,
    });
  };

  const handleCheckboxChange = (fieldName, value, checked) => {
    setFormData((prev) => {
      const currentArray = prev[fieldName];
      const newArray = checked
        ? [...currentArray, value]
        : currentArray.filter((item) => item !== value);

      const updates = { [fieldName]: newArray };

      // Clear custom text when "Other" is unchecked
      if (!checked && value === 'Other') {
        if (fieldName === 'govOrganizations') updates.otherGovOrganization = '';
        if (fieldName === 'difficultyReasons') updates.otherDifficultyReason = '';
      }

      // Handle gov organizations dependencies
      if (fieldName === 'govOrganizations') {
        const hasDoss = newArray.some((org) =>
          org.includes('Department of Social Services')
        );
        const hasMinistry = newArray.some((org) =>
          org.includes('Ministry of Skill Development')
        );

        if (!hasDoss) {
          updates.dossInstitutions = [];
          updates.completedDossCourses = [];
        }
        if (!hasMinistry) {
          updates.ministryInstitutions = [];
          updates.completedMinistryCourses = '';
        }
      }

      return { ...prev, ...updates };
    });
  };

  const shouldShowTrainingDetails = () => {
    return ['Completed', 'Currently doing', 'Discontinued'].includes(
      formData.vocationalStatus
    );
  };

  const shouldShowCompletedSection = () => {
    return formData.vocationalStatus === 'Completed';
  };

  const shouldShowDiscontinuedReason = () => {
    return formData.vocationalStatus === 'Discontinued';
  };

  const shouldShowNotJoinedSection = () => {
    return formData.vocationalStatus === 'Not joined';
  };

  const shouldShowOrganizationName = () => {
    return (
      formData.trainingKind === 'NGO' || formData.trainingKind === 'Private'
    );
  };

  const shouldShowGovOrganizations = () => {
    return formData.trainingKind === 'Government';
  };

  const shouldShowDossInstitutions = () => {
    return formData.govOrganizations.some((org) =>
      org.includes('Department of Social Services')
    );
  };

  const shouldShowMinistryInstitutions = () => {
    return formData.govOrganizations.some((org) =>
      org.includes('Ministry of Skill Development')
    );
  };

  const shouldShowNatureOfProfession = () => {
    return formData.relatedOccupation === 'Yes';
  };

  const shouldShowUnrelatedReason = () => {
    return formData.relatedOccupation === 'Engages in unrelated occupation';
  };

  const shouldShowDifficultyReasons = () => {
    return formData.hasDifficulty === 'Yes';
  };

  const shouldShowOtherGovOrganization = () =>
    formData.govOrganizations.includes('Other');

  const shouldShowOtherDifficultyReason = () =>
    formData.difficultyReasons.includes('Other');

  const arrayToStringWithOther = (arr, otherValue) => {
    const filtered = arr.filter((item) => item !== 'Other');
    if (arr.includes('Other') && otherValue) filtered.push(`Other: ${otherValue}`);
    return filtered.join(', ');
  };

  const hasChanges = () => {
    const arrayToString = (arr) => arr.sort().join(', ');

    const current = {
      ...formData,
      govOrganizations: arrayToString(formData.govOrganizations),
      dossInstitutions: arrayToString(formData.dossInstitutions),
      ministryInstitutions: arrayToString(formData.ministryInstitutions),
      completedDossCourses: arrayToString(formData.completedDossCourses),
      difficultyReasons: arrayToString(formData.difficultyReasons),
    };

    const original = {
      ...originalData,
      govOrganizations: arrayToString(originalData.govOrganizations),
      dossInstitutions: arrayToString(originalData.dossInstitutions),
      ministryInstitutions: arrayToString(originalData.ministryInstitutions),
      completedDossCourses: arrayToString(originalData.completedDossCourses),
      difficultyReasons: arrayToString(originalData.difficultyReasons),
    };

    return JSON.stringify(current) !== JSON.stringify(original);
  };

  const handleSave = async () => {
    if (!formData.vocationalStatus) {
      setAlert({
        show: true,
        message: 'Please select vocational training status.',
        type: 'warning',
      });
      return;
    }

    if (!hasChanges()) {
      setAlert({
        show: true,
        message: 'No changes detected.',
        type: 'warning',
      });
      return;
    }

    if (!(await confirmWithConsent())) {
      return;
    }

    setSaving(true);

    try {
      const updateData = {
        uniqueId: recordData.uniqueId,
        vocationalStatus: formData.vocationalStatus,
        trainingKind: formData.trainingKind,
        organizationName: formData.organizationName,
        govOrganizations: arrayToStringWithOther(formData.govOrganizations, formData.otherGovOrganization),
        dossInstitutions: formData.dossInstitutions.join(', '),
        ministryInstitutions: formData.ministryInstitutions.join(', '),
        completedDossCourses: formData.completedDossCourses.join(', '),
        completedMinistryCourses: formData.completedMinistryCourses,
        relatedOccupation: formData.relatedOccupation,
        natureOfProfession: formData.natureOfProfession,
        unrelatedReason: formData.unrelatedReason,
        discontinuedReason: formData.discontinuedReason === 'Other' && formData.otherDiscontinuedReason
          ? formData.otherDiscontinuedReason
          : formData.discontinuedReason,
        notFollowingReason: formData.notFollowingReason === 'Other' && formData.otherNotFollowingReason
          ? formData.otherNotFollowingReason
          : formData.notFollowingReason,
        hasDifficulty: formData.hasDifficulty,
        difficultyReasons: arrayToStringWithOther(formData.difficultyReasons, formData.otherDifficultyReason),
        needsCareerGuidance: formData.needsCareerGuidance,
        sheetId: recordData.sheetId,
        rowIndex: recordData.rowIndex,
      };

      const response = await AppScriptAPI.updateVocationalTrainingDetails(
        updateData
      );

      if (response.success) {
        setAlert({
          show: true,
          message: 'Vocational training details updated successfully.',
          type: 'success',
        });
        setOriginalData(JSON.parse(JSON.stringify(formData)));
      } else {
        setAlert({
          show: true,
          message:
            'Failed to update: ' + (response.message || 'Unknown error'),
          type: 'danger',
        });
      }
    } catch (error) {
      setAlert({
        show: true,
        message: 'An error occurred: ' + error.message,
        type: 'danger',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleNext = () => {
    navigate(`/update/economic/${recordId}?sheetId=${sheetId}`);
  };

  const handlePrevious = () => {
    navigate(`/update/education/${recordId}?sheetId=${sheetId}`);
  };

  if (loading) {
    return (
      <UpdateLayout
        currentStep={7}
        title="Update Vocational Training Details"
        recordId={recordId}
        sheetId={sheetId}
      >
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2">Loading vocational training details...</p>
        </div>
      </UpdateLayout>
    );
  }

  if (!recordData) {
    return (
      <UpdateLayout
        currentStep={7}
        title="Update Vocational Training Details"
        recordId={recordId}
        sheetId={sheetId}
      >
        <Alert variant="danger">
          <strong>Error!</strong>
          <br />
          {alert.message || 'Failed to load vocational training details.'}
        </Alert>
      </UpdateLayout>
    );
  }

  return (
    <UpdateLayout
      currentStep={7}
      title="Update Vocational Training Details"
      recordId={recordId}
      sheetId={sheetId}
      onSave={handleSave}
      onNext={handleNext}
      onPrevious={handlePrevious}
      isSaving={saving}
      hasChanges={hasChanges()}
      canEdit={canEdit}
    >
      {ConsentModalJSX}
      <AppToast
        show={alert.show}
        type={alert.type}
        message={alert.message}
        onClose={() => setAlert(a => ({ ...a, show: false }))}
      />

      <Form>
        <div className="section-header">Record Information (Read Only)</div>

        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Unique ID</Form.Label>
              <Form.Control
                type="text"
                value={formData.uniqueId}
                className="readonly-field"
                readOnly
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control
                type="text"
                value={formData.name}
                className="readonly-field"
                readOnly
              />
            </Form.Group>
          </Col>
        </Row>

        <div className="section-header">
          Vocational Training Status (Editable)
        </div>

        <Form.Group className="mb-3">
          <Form.Label>
            Status of Vocational Training <span className="text-danger">*</span>
          </Form.Label>
          <Form.Select
            name="vocationalStatus"
            value={formData.vocationalStatus}
            onChange={handleInputChange}
            required
            disabled={!canEdit}
          >
            <option value="">Select...</option>
            {legendData?.vocationalStatus?.map((item, index) => (
              <option key={index} value={item}>
                {item}
              </option>
            ))}
          </Form.Select>
        </Form.Group>

        {/* Training Details Section */}
        {shouldShowTrainingDetails() && (
          <div className="conditional-field">
            <Form.Group className="mb-3">
              <Form.Label>
                What kind of vocational training is doing/was obtained?{' '}
                <span className="text-danger">*</span>
              </Form.Label>
              <Form.Select
                name="trainingKind"
                value={formData.trainingKind}
                onChange={handleInputChange}
                disabled={!canEdit}
              >
                <option value="">Select...</option>
                <option value="Government">Government</option>
                <option value="NGO">NGO</option>
                <option value="Private">Private</option>
              </Form.Select>
            </Form.Group>

            {shouldShowOrganizationName() && (
              <Form.Group className="mb-3 conditional-field">
                <Form.Label>Organization Name</Form.Label>
                <Form.Control
                  type="text"
                  name="organizationName"
                  value={formData.organizationName}
                  onChange={handleInputChange}
                  placeholder="Enter organization name"
                  disabled={!canEdit}
                />
                <span className="info-badge">For NGO or Private training</span>
              </Form.Group>
            )}

            {shouldShowGovOrganizations() && (
              <Form.Group className="mb-3 conditional-field">
                <Form.Label>
                  Training received from which organization? (Select all that
                  apply)
                </Form.Label>
                <div className="checkbox-group">
                  {legendData?.govOrganizations?.map((item, index) => (
                    <Form.Check
                      key={index}
                      type="checkbox"
                      id={`govOrg-${index}`}
                      label={item}
                      checked={formData.govOrganizations.includes(item)}
                      onChange={(e) =>
                        handleCheckboxChange(
                          'govOrganizations',
                          item,
                          e.target.checked
                        )
                      }
                      disabled={!canEdit}
                    />
                  ))}
                </div>
                {shouldShowOtherGovOrganization() && (
                  <Form.Control
                    className="mt-2"
                    type="text"
                    name="otherGovOrganization"
                    value={formData.otherGovOrganization}
                    onChange={handleInputChange}
                    placeholder="Please specify other organization"
                    disabled={!canEdit}
                  />
                )}
                <span className="info-badge">For Government training</span>
              </Form.Group>
            )}

            {shouldShowDossInstitutions() && (
              <>
                <Form.Group className="mb-3 conditional-field">
                  <Form.Label>
                    Vocational Training Institutions of the Department of Social
                    Services (Select all that apply)
                  </Form.Label>
                  <div className="checkbox-group">
                    {legendData?.dossInstitutions?.map((item, index) => (
                      <Form.Check
                        key={index}
                        type="checkbox"
                        id={`dossInst-${index}`}
                        label={item}
                        checked={formData.dossInstitutions.includes(item)}
                        onChange={(e) =>
                          handleCheckboxChange(
                            'dossInstitutions',
                            item,
                            e.target.checked
                          )
                        }
                        disabled={!canEdit}
                      />
                    ))}
                  </div>
                </Form.Group>

                <Form.Group className="mb-3 conditional-field">
                  <Form.Label>
                    Completed Vocational Training Course from DoSS (Select all
                    that apply)
                  </Form.Label>
                  <div className="checkbox-group">
                    {legendData?.completedDossCourses?.map((item, index) => (
                      <Form.Check
                        key={index}
                        type="checkbox"
                        id={`dossCourse-${index}`}
                        label={item}
                        checked={formData.completedDossCourses.includes(item)}
                        onChange={(e) =>
                          handleCheckboxChange(
                            'completedDossCourses',
                            item,
                            e.target.checked
                          )
                        }
                        disabled={!canEdit}
                      />
                    ))}
                  </div>
                  <span className="info-badge">
                    Shown when DoSS is selected
                  </span>
                </Form.Group>
              </>
            )}

            {shouldShowMinistryInstitutions() && (
              <>
                <Form.Group className="mb-3 conditional-field">
                  <Form.Label>
                    Vocational Training Institutions of the Ministry of Skill
                    Development (Select all that apply)
                  </Form.Label>
                  <div className="checkbox-group">
                    {legendData?.ministryInstitutions?.map((item, index) => (
                      <Form.Check
                        key={index}
                        type="checkbox"
                        id={`ministryInst-${index}`}
                        label={item}
                        checked={formData.ministryInstitutions.includes(item)}
                        onChange={(e) =>
                          handleCheckboxChange(
                            'ministryInstitutions',
                            item,
                            e.target.checked
                          )
                        }
                        disabled={!canEdit}
                      />
                    ))}
                  </div>
                </Form.Group>

                <Form.Group className="mb-3 conditional-field">
                  <Form.Label>
                    Completed Vocational Training from Ministry of Skill
                    Development
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="completedMinistryCourses"
                    value={formData.completedMinistryCourses}
                    onChange={handleInputChange}
                    placeholder="Enter completed course name"
                    disabled={!canEdit}
                  />
                  <span className="info-badge">
                    Shown when Ministry of Skill Development is selected
                  </span>
                </Form.Group>
              </>
            )}
          </div>
        )}

        {/* Completed Section */}
        {shouldShowCompletedSection() && (
          <div className="conditional-field">
            <Form.Group className="mb-3">
              <Form.Label>
                Are you also involved in the occupation related to that
                vocational training you obtained?
              </Form.Label>
              <div>
                <Form.Check
                  type="radio"
                  label="Yes"
                  name="relatedOccupation"
                  value="Yes"
                  checked={formData.relatedOccupation === 'Yes'}
                  onChange={handleInputChange}
                  disabled={!canEdit}
                />
                <Form.Check
                  type="radio"
                  label="No"
                  name="relatedOccupation"
                  value="No"
                  checked={formData.relatedOccupation === 'No'}
                  onChange={handleInputChange}
                  disabled={!canEdit}
                />
                <Form.Check
                  type="radio"
                  label="Engages in unrelated occupation"
                  name="relatedOccupation"
                  value="Engages in unrelated occupation"
                  checked={
                    formData.relatedOccupation ===
                    'Engages in unrelated occupation'
                  }
                  onChange={handleInputChange}
                  disabled={!canEdit}
                />
              </div>
            </Form.Group>

            {shouldShowNatureOfProfession() && (
              <Form.Group className="mb-3 conditional-field">
                <Form.Label>Nature of Profession</Form.Label>
                <Form.Select
                  name="natureOfProfession"
                  value={formData.natureOfProfession}
                  onChange={handleInputChange}
                  disabled={!canEdit}
                >
                  <option value="">Select...</option>
                  {legendData?.natureOfProfession?.map((item, index) => (
                    <option key={index} value={item}>
                      {item}
                    </option>
                  ))}
                </Form.Select>
                <span className="info-badge">
                  For those working in related occupation
                </span>
              </Form.Group>
            )}

            {shouldShowUnrelatedReason() && (
              <Form.Group className="mb-3 conditional-field">
                <Form.Label>
                  Reason for Engaging in occupation unrelated to the training
                  you obtained
                </Form.Label>
                <Form.Select
                  name="unrelatedReason"
                  value={formData.unrelatedReason}
                  onChange={handleInputChange}
                  disabled={!canEdit}
                >
                  <option value="">Select...</option>
                  {legendData?.unrelatedReason?.map((item, index) => (
                    <option key={index} value={item}>
                      {item}
                    </option>
                  ))}
                </Form.Select>
                <span className="info-badge">
                  For those in unrelated occupation
                </span>
              </Form.Group>
            )}
          </div>
        )}

        {/* Discontinued Reason */}
        {shouldShowDiscontinuedReason() && (
          <Form.Group className="mb-3 conditional-field">
            <Form.Label>
              Reason for non-completion/giving up the course
            </Form.Label>
            <SelectWithOther
              name="discontinuedReason"
              value={formData.discontinuedReason}
              otherName="otherDiscontinuedReason"
              otherValue={formData.otherDiscontinuedReason}
              options={legendData?.discontinuedReason || []}
              onChange={handleInputChange}
              disabled={!canEdit}
              otherPlaceholder="Please specify reason"
            />
            <span className="info-badge">For discontinued training</span>
          </Form.Group>
        )}

        {/* Not Joined Section */}
        {shouldShowNotJoinedSection() && (
          <div className="conditional-field">
            <Form.Group className="mb-3">
              <Form.Label>
                Reason for not following any vocational training course
              </Form.Label>
              <SelectWithOther
                name="notFollowingReason"
                value={formData.notFollowingReason}
                otherName="otherNotFollowingReason"
                otherValue={formData.otherNotFollowingReason}
                options={legendData?.notFollowingReason || []}
                onChange={handleInputChange}
                disabled={!canEdit}
                otherPlaceholder="Please specify reason"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>
                Do you have a difficulty to get any vocational training?
              </Form.Label>
              <div>
                <Form.Check
                  type="radio"
                  label="Yes"
                  name="hasDifficulty"
                  value="Yes"
                  checked={formData.hasDifficulty === 'Yes'}
                  onChange={handleInputChange}
                  disabled={!canEdit}
                />
                <Form.Check
                  type="radio"
                  label="No"
                  name="hasDifficulty"
                  value="No"
                  checked={formData.hasDifficulty === 'No'}
                  onChange={handleInputChange}
                  disabled={!canEdit}
                />
              </div>
            </Form.Group>

            {shouldShowDifficultyReasons() && (
              <Form.Group className="mb-3 conditional-field">
                <Form.Label>
                  Reasons for having difficult to get vocational training
                  (Select all that apply)
                </Form.Label>
                <div className="checkbox-group">
                  {legendData?.difficultyReasons?.map((item, index) => (
                    <Form.Check
                      key={index}
                      type="checkbox"
                      id={`diffReason-${index}`}
                      label={item}
                      checked={formData.difficultyReasons.includes(item)}
                      onChange={(e) =>
                        handleCheckboxChange(
                          'difficultyReasons',
                          item,
                          e.target.checked
                        )
                      }
                      disabled={!canEdit}
                    />
                  ))}
                </div>
                {shouldShowOtherDifficultyReason() && (
                  <Form.Control
                    className="mt-2"
                    type="text"
                    name="otherDifficultyReason"
                    value={formData.otherDifficultyReason}
                    onChange={handleInputChange}
                    placeholder="Please specify other reason"
                    disabled={!canEdit}
                  />
                )}
              </Form.Group>
            )}
          </div>
        )}

        {/* Career Guidance */}
        <div className="section-header">Career Guidance</div>

        <Form.Group className="mb-3">
          <Form.Label>Do you need any career guidance?</Form.Label>
          <div>
            <Form.Check
              type="radio"
              label="Yes"
              name="needsCareerGuidance"
              value="Yes"
              checked={formData.needsCareerGuidance === 'Yes'}
              onChange={handleInputChange}
              disabled={!canEdit}
            />
            <Form.Check
              type="radio"
              label="No"
              name="needsCareerGuidance"
              value="No"
              checked={formData.needsCareerGuidance === 'No'}
              onChange={handleInputChange}
              disabled={!canEdit}
            />
          </div>
        </Form.Group>
      </Form>
    </UpdateLayout>
  );
}

export default UpdateVocational;
